/**
 * Verification Slice
 * Tracks the verification state machine for each verification type
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type {
  VerificationStatus,
  VerificationType,
  VerificationMethod,
  VerificationStatusValue,
} from '@/types/onboarding';
import { INITIAL_VERIFICATION_STATUS } from '@/types/onboarding';

export interface VerificationState {
  pan: VerificationStatus;
  aadhaar: VerificationStatus;
  gstin: VerificationStatus;
  bank: VerificationStatus;
  cin: VerificationStatus;
  din: Record<string, VerificationStatus>; // keyed by partner ID
}

const initialState: VerificationState = {
  pan: { ...INITIAL_VERIFICATION_STATUS },
  aadhaar: { ...INITIAL_VERIFICATION_STATUS },
  gstin: { ...INITIAL_VERIFICATION_STATUS },
  bank: { ...INITIAL_VERIFICATION_STATUS },
  cin: { ...INITIAL_VERIFICATION_STATUS },
  din: {},
};

const verificationSlice = createSlice({
  name: 'verification',
  initialState,
  reducers: {
    // ── Set status for a standard verification type ──────
    setVerificationStatus: (
      state,
      action: PayloadAction<{
        type: VerificationType;
        status: VerificationStatusValue;
        method?: VerificationMethod | null;
        confidence?: number;
        data?: Record<string, any>;
        documentUrl?: string | null;
        requiresManualReview?: boolean;
        errorMessage?: string | null;
      }>
    ) => {
      const { type, status, method, confidence, data, documentUrl, requiresManualReview, errorMessage } = action.payload;
      if (type === 'din') return; // use setDinVerification for DIN

      const current = state[type] as VerificationStatus;
      state[type as Exclude<VerificationType, 'din'>] = {
        ...current,
        status,
        method: method ?? current.method,
        confidence: confidence ?? current.confidence,
        data: data ?? current.data,
        documentUrl: documentUrl ?? current.documentUrl,
        requiresManualReview: requiresManualReview ?? current.requiresManualReview,
        errorMessage: errorMessage ?? current.errorMessage,
        timestamp: new Date().toISOString(),
      };
    },

    // ── Set full verification result (e.g. after API success) ──
    setVerificationResult: (
      state,
      action: PayloadAction<{
        type: Exclude<VerificationType, 'din'>;
        result: VerificationStatus;
      }>
    ) => {
      state[action.payload.type] = action.payload.result;
    },

    // ── DIN per-partner ──────────────────────────────────
    setDinVerification: (
      state,
      action: PayloadAction<{
        partnerId: string;
        status: VerificationStatus;
      }>
    ) => {
      state.din[action.payload.partnerId] = action.payload.status;
    },

    // ── Reset single type ────────────────────────────────
    resetVerification: (state, action: PayloadAction<Exclude<VerificationType, 'din'>>) => {
      state[action.payload] = { ...INITIAL_VERIFICATION_STATUS };
    },

    // ── Reset all ────────────────────────────────────────
    resetAllVerifications: () => initialState,

    // ── Hydrate from Firestore on login/session restore ──
    hydrateFromFirestore: (
      state,
      action: PayloadAction<{
        pan?: Partial<VerificationStatus>;
        bank?: Partial<VerificationStatus>;
        aadhaar?: Partial<VerificationStatus>;
        gstin?: Partial<VerificationStatus>;
      }>
    ) => {
      const { pan, bank, aadhaar, gstin } = action.payload;
      if (pan) state.pan = { ...INITIAL_VERIFICATION_STATUS, ...pan } as VerificationStatus;
      if (bank) state.bank = { ...INITIAL_VERIFICATION_STATUS, ...bank } as VerificationStatus;
      if (aadhaar) state.aadhaar = { ...INITIAL_VERIFICATION_STATUS, ...aadhaar } as VerificationStatus;
      if (gstin) state.gstin = { ...INITIAL_VERIFICATION_STATUS, ...gstin } as VerificationStatus;
    },
  },
});

export const {
  setVerificationStatus,
  setVerificationResult,
  setDinVerification,
  resetVerification,
  resetAllVerifications,
  hydrateFromFirestore,
} = verificationSlice.actions;

export default verificationSlice.reducer;
